// src/components/layout/AppLayout.tsx
import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { Link, useLocation } from 'react-router-dom'
import { 
  Menu, 
  X, 
  BarChart3, 
  Package, 
  BookOpen, 
  MapPin, 
  TrendingUp,
  Users,
  Settings as SettingsIcon,
  CreditCard,
  Shield,
  LogOut,
  Brain
} from 'lucide-react'
import { Button } from '../ui/button'
import { Logo } from '../ui/Logo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

interface AppLayoutProps {
  children: React.ReactNode
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { profile, organization, signOut, canAccess } = useAuth()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navigationTop = [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
    { name: 'Inventory', href: '/inventory', icon: Package, requireRole: 'company_admin' },
    { name: 'Catalog: Products', href: '/catalog/products', icon: BookOpen, requireRole: 'staff' },
    { name: 'Locations', href: '/locations', icon: MapPin, requireRole: 'staff' },
    { name: 'Reports', href: '/reports', icon: TrendingUp, requireRole: 'company_admin' },
    { name: 'Intelligence', href: '/reports/intelligence', icon: Brain, requireRole: 'company_admin' },
  ]
  const navigationBottom = [
    { name: 'Team', href: '/team', icon: Users, requireRole: 'company_admin' },
    { name: 'Settings', href: '/settings', icon: SettingsIcon },
    { name: 'Subscription', href: '/subscription', icon: CreditCard, requireRole: 'company_admin' },
    { name: 'Admin', href: '/admin', icon: Shield, requireRole: 'super_admin' },
  ]

  // Filter navigation based on user role
  const filterByRole = (item: { requireRole?: string }) => {
    if (!item.requireRole) return true
    
    const roleHierarchy = {
      'staff': 1,
      'company_admin': 2,
      'super_admin': 3
    }
    
    const userRole = profile?.role?.toLowerCase() // Normalize role
    
    const userLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0
    const requiredLevel = roleHierarchy[item.requireRole as keyof typeof roleHierarchy] || 0
    
    return userLevel >= requiredLevel
  }
  const filteredTop = navigationTop.filter(filterByRole)
  let filteredBottom = navigationBottom.filter(filterByRole)
  // Ensure Admin is visible for super_admin even if role state lags briefly
  const isSuperAdmin = canAccess('super_admin') || profile?.role === 'super_admin'
  if (isSuperAdmin && !filteredBottom.find(i => i.href === '/admin')) {
    filteredBottom = [...filteredBottom, { name: 'Admin', href: '/admin', icon: Shield, requireRole: 'super_admin' }]
  }
  // admin visibility handled via isSuperAdmin and filteredBottom checks

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4">
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Logo
              showText
              className="text-foreground"
              iconClassName="h-8 w-8"
              labelClassName="text-lg sm:text-xl text-foreground"
            />
            {organization && (
              <span className="hidden sm:block text-sm text-muted-foreground">
                {organization.name}
              </span>
            )}
          </div>
          
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">Menu ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]">
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <div className="px-2 pb-1 text-xs text-muted-foreground break-words">
                  <div className="truncate">{profile?.email}</div>
                  <div className="mt-0.5">Role: <span className="uppercase">{profile?.role}</span></div>
                </div>
                <DropdownMenuSeparator />
                {navigationBottom.filter(filterByRole).map((item) => {
                  const Icon = item.icon
                  return (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className="flex items-center">
                        <Icon className="h-4 w-4 mr-2" />
                        <span>{item.name}</span>
                      </Link>
                    </DropdownMenuItem>
                  )
                })}
                {isSuperAdmin && !navigationBottom.filter(filterByRole).find(i => i.href === '/admin') && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      <span>Admin</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); signOut() }}>
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <nav className="hidden md:block w-64 bg-card border-r border-border min-h-screen relative circuit-pattern">
          <div className="p-4 h-full flex flex-col relative z-10">
            <ul className="space-y-2">
              {filteredTop.map((item) => {
                const isActive = location.pathname === item.href
                const Icon = item.icon
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/20 text-primary-foreground border-l-2 border-l-primary glow-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <div className={`mr-3 p-1.5 rounded-lg ${isActive ? 'bg-primary/10' : ''}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      {item.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
            {/* Moved Team/Settings/Admin to top-right dropdown */}
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setMobileMenuOpen(false)} />
            <nav className="fixed top-0 left-0 bottom-0 flex flex-col w-64 max-w-xs bg-card border-r border-border">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Navigation</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 p-4 flex flex-col">
                <ul className="space-y-2">
                  {filteredTop.map((item) => {
                    const isActive = location.pathname === item.href
                    const Icon = item.icon
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        >
                          <Icon className="mr-3 h-5 w-5" />
                          {item.name}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
                {/* Moved Team/Settings/Admin to top-right dropdown */}
              </div>
            </nav>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 w-0 overflow-auto bg-background">
          {children}
        </main>
      </div>
    </div>
  )
}
